import { Link } from '@inertiajs/react';
import type { MouseEvent, ReactNode } from 'react';
import { isTrackablePath, normalizePath } from '../../lib/menuPath';
import { cn } from '../ui/utils';

type NexusNavLinkProps = {
    href: string | null | undefined;
    children: ReactNode;
    className?: string;
    title?: string;
    target?: string | null;
    rel?: string;
    preserveScroll?: boolean;
    preserveState?: boolean;
    onClick?: (event: MouseEvent<Element>) => void;
    'aria-current'?: 'page' | 'step' | 'location' | 'date' | 'time' | 'true' | 'false' | boolean;
};

function flashExternalPreload(): void {
    if (typeof document === 'undefined') {
        return;
    }
    document.documentElement.classList.add('nx-external-preload');
}

/**
 * Link inteligente: rotas `/nexus*` usam Inertia (SPA, sem prefetch).
 * Demais URLs usam `<a>` com flash de pré-load antes do unload.
 */
export default function NexusNavLink({
    href,
    children,
    className,
    title,
    preserveScroll,
    preserveState,
    target,
    rel,
    onClick,
    'aria-current': ariaCurrent,
}: NexusNavLinkProps) {
    const raw = href?.trim() || '#';
    const isHash =
        raw === '#' ||
        raw.startsWith('javascript:') ||
        raw.startsWith('mailto:') ||
        raw.startsWith('tel:');
    const path = normalizePath(raw);
    const isNexus = !isHash && isTrackablePath(path);
    const isExternalTab = target === '_blank';

    if (isNexus && !isExternalTab) {
        return (
            <Link
                href={path}
                className={className}
                title={title}
                prefetch={false}
                preserveScroll={preserveScroll}
                preserveState={preserveState}
                onClick={onClick}
                aria-current={ariaCurrent}
            >
                {children}
            </Link>
        );
    }

    const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
        onClick?.(event);
        if (event.defaultPrevented || isHash || isExternalTab) {
            return;
        }
        flashExternalPreload();
    };

    return (
        <a
            href={raw}
            className={cn(className)}
            title={title}
            target={target ?? undefined}
            rel={
                rel ??
                (target === '_blank' ? 'noopener noreferrer' : undefined)
            }
            onClick={handleClick}
            aria-current={ariaCurrent}
        >
            {children}
        </a>
    );
}
